import { ActionReducer, INIT, UPDATE, createReducer, on } from '@ngrx/store';
import {
  loadCompanies,
  loadCompaniesSuccess,
  loadCompaniesFailure,
  loadCompanyUnits,
  loadCompanyUnitsSuccess,
  loadCompanyUnitsFailure,
  changeSelectedCompany,
  changeSelectedCompanyUnit,
  changeShowHidden,
  changeSelectedRole,
  setAccountId,
} from './company-context.actions';
import { BdoServicePage } from 'src/app/services/bdoservice/common/model/bdo-service-page.model';
import { CompanyUnit } from 'src/app/services/bdoservice/companies/models/company-unit.model';
import { RoleDropdownItem } from './company-context-select.component';
import { BdoCompanyItem } from 'src/app/services/bdoservice/companies/models/bdo-company-item';


export interface CompanyContextState {
  companies: BdoCompanyItem[] | null;
  companyUnits: BdoServicePage<CompanyUnit> | null;
  selectedCompany: BdoCompanyItem | null;
  selectedRole: RoleDropdownItem | null;
  selectedCompanyUnit: CompanyUnit | null;
  showHidden: boolean;
  loadingCompanies: boolean;
  loadingCompanyUnits: boolean;
  error: string | null;
  accountId: string | null;
}

export const initialState: CompanyContextState = {
  companies: null,
  companyUnits: null,
  selectedCompany: null,
  selectedRole: null,
  selectedCompanyUnit: null,
  showHidden: false,
  loadingCompanies: true,
  loadingCompanyUnits: true,
  error: null,
  accountId: null
};

export const companyContextReducer = createReducer(
  initialState,
  // Handle loading companies
  on(loadCompanies, (state) => ({
    ...state,
    loadingCompanies: true,
    error: null
  })),
  on(loadCompaniesSuccess, (state, { items }) => ({
    ...state,
    loadingCompanies: false,
    companies: items
  })),
  on(loadCompaniesFailure, (state, { error }) => ({
    ...state,
    loadingCompanies: false,
    error
  })),

  // Handle loading company units
  on(loadCompanyUnits, (state) => ({
    ...state,
    loadingCompanyUnits: true,
    error: null
  })),
  on(loadCompanyUnitsSuccess, (state, { page }) => ({
    ...state,
    loadingCompanyUnits: false,
    companyUnits: page
  })),
  on(loadCompanyUnitsFailure, (state, { error }) => ({
    ...state,
    loadingCompanyUnits: false,
    error
  })),

  // Handle change selected company
  on(changeSelectedCompany, (state, { item }) => ({
    ...state,
    selectedCompany: item,
    companyUnits: null,
    loadingCompanyUnits: true
  })),

  // Handle change selected company unit
  on(changeSelectedCompanyUnit, (state, { companyUnit }) => ({
    ...state,
    selectedCompanyUnit: companyUnit
  })),

  // Handle toggle show hidden company units
  on(changeShowHidden, (state, { showHidden }) => ({
    ...state,
    showHidden
  })),

  on(changeSelectedRole, (state, { role }) => ({
    ...state,
    selectedRole: role
  })),

  on(setAccountId, (state, { accountId }) => ({
    ...state,
    accountId
  }))
);

export function localStorageMetaReducer<T>(reducer: ActionReducer<T>): ActionReducer<T> {
  return function(state, action) {
    // On INIT or UPDATE, restore the state from localStorage
    if (action.type === INIT || action.type === UPDATE) {
      const savedState = localStorage.getItem('companyContext');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          // Return the restored state, ensuring that the reducer initializes with this state
          return { ...state, ...parsedState };
        } catch {
          // If there's an error parsing the state, remove the corrupted state
          localStorage.removeItem('companyContext');
        }
      }
    }

    // Check if account changed - clear state if different user logged in
    if (action.type === setAccountId.type) {
      const savedState = localStorage.getItem('companyContext');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          const storedAccountId = parsedState?.companyContext?.accountId;
          const newAccountId = (action as ReturnType<typeof setAccountId>).accountId;

          if (storedAccountId && storedAccountId !== newAccountId) {
            console.log('[CompanyContext] Konto zostało zmienione - czyszczenie stanu podmiotu i MPD', {
              previousAccountId: storedAccountId,
              newAccountId: newAccountId
            });
            // Clear localStorage and return initial state with new accountId
            localStorage.removeItem('companyContext');
            const clearedState = reducer(undefined, { type: INIT });
            const stateWithNewAccountId = reducer(clearedState, action);
            localStorage.setItem('companyContext', JSON.stringify(stateWithNewAccountId));
            return stateWithNewAccountId;
          }
        } catch {
          localStorage.removeItem('companyContext');
        }
      }
    }

    // Process the action with the current reducer
    const nextState = reducer(state, action);

    // Save the new state to localStorage
    localStorage.setItem('companyContext', JSON.stringify(nextState));

    return nextState;
  };

}
